import {
  awaitRender,
  div, fitText,
  hide,
  setText,
  show
} from '@megaphonenpm/mputils/src/DOM.js';
import {cloneInstance} from "../GWDService.js";
import {cascadingGet, getAverageVote, isNil, notNil} from '@megaphonenpm/mputils/src/utils.js';
import {MegaScreen} from "../MegaScreen.js";
import {play} from "../Animation.js";
import {ScreenComponent} from "./ScreenComponent.js";
import {InstrumentValueLabelComponent} from "./InstrumentValueLabelComponent.js";

/**
 * A generic class for creating instrument with min/max labels, an indicator, a value label, and a final result.
 * Used to create Guess Gauge and Mood Meter answer components.
 */
export class InstrumentComponent extends ScreenComponent {
  constructor (themeID, instrumentName, indicator) {
    super(themeID, ['minmax_label_txt'], {indicator: indicator,
      instrumentName: instrumentName.toLowerCase()});
  }

  build () {
    super.build();

    // Pre-flight check
    if (!this.valid) {
      return;
    }

    // Theme settings
    this.direction = this.getThemeSetting(`design.products.${this.instrumentName}.screen.main.direction`, 'horizontal');

    // Outer-most container
    this.view = div(`answers${this.instrumentName}Container`, 'answersContainer');
    this.view.classList.add('constrainHeight');

    // Indicate the states during which the view should be visible
    this.view.classList.add('state_scene', 'state_scene_result');
    this.view.classList.add(`product_${this.instrumentName}`);

    // Final result
    this.finalResultContainer = div(null, `${this.instrumentName}FinalResultContainer`);
    this.finalResultContainer.classList.add('flexExpandingItem', 'flex', 'flexCenterChildren');
    this.result = cloneInstance(this.themeID, `${this.instrumentName}Result`);
    if (isNil(this.result)) {
      // No instrument-specific "scene result" was found, so attempt to clone the generic one.
      // In themes that do not have a custom result layout for Mood Meter, Guess Gauge, and Five Stars,
      // the "averageVoteResult" is used for all of those products.
      this.result = cloneInstance(this.themeID, 'averageVoteResult');
    }
    if (this.result) {
      this.finalResultContainer.append(this.result);
      setText(this.result.querySelectorAll(`.${this.instrumentName}_audience_average_txt, .audience_average_txt`),
        this.getLocaleString('audience_average'));
    }

    // Outer container for indicator (e.g., Guess Gauge slider, Mood Meter dial)
    this.indicatorContainer = div(null, 'indicatorContainer');
    this.indicatorContainer.classList.add('flexExpandingItem', 'flex', 'constrainHeight', 'flexCenterJustified');
    if (this.direction === 'horizontal') {
      this.indicatorContainer.style.height = '100%';
    }

    // Apply indicator external theme settings
    let marginTop = this.getThemeSetting(`design.products.${this.instrumentName}.screen.main.margin.top`, 0);
    this.indicatorContainer.style.marginTop = `${marginTop}px`;

    let valign = this.getThemeSetting(`design.products.${this.instrumentName}.screen.main.align.vertical`);
    if (valign === 'top') {
      this.indicatorContainer.classList.add('flexStartAlign');
    } else if (valign === 'center') {
      this.indicatorContainer.classList.add('flexCenterAlign');
    } else {
      this.indicatorContainer.classList.add('flexEndAlign');
    }
    let backgroundColor = this.getThemeSetting(`design.products.${this.instrumentName}.screen.main.background_color`);
    if (notNil(backgroundColor)) {
      this.view.style.background = backgroundColor;
    }
    // In the newer theme spec, the correct border setting is "...main.border.stroke"
    let borderStroke = this.getThemeSetting(`design.products.${this.instrumentName}.screen.main.border.stroke`);

    // In earlier, legacy versions of the theme spec, border settings were broken into "...border.color" and "...border.width"
    let borderColor = this.getThemeSetting(`design.products.${this.instrumentName}.screen.main.border.color`);
    let borderWidth = this.getThemeSetting(`design.products.${this.instrumentName}.screen.main.border.width`, 1);

    if (notNil(borderStroke)) {
      // Newer spec
      this.view.style.border = borderStroke;
    } else if (notNil(borderColor)) {
      // Older spec
      this.view.style.borderColor = borderColor;
      this.view.style.borderWidth = `${borderWidth}px`;
      this.view.style.borderStyle = 'solid';
    }


    let indicatorWidth = this.getThemeSetting(`design.products.${this.instrumentName}.screen.indicator.size.width`);
    let indicatorHeight = this.getThemeSetting(`design.products.${this.instrumentName}.screen.indicator.size.height`);
    if (notNil(indicatorWidth && notNil(indicatorHeight))) {
      this.indicator.setSize(indicatorWidth, indicatorHeight);
    }
    this.indicator.view.style.margin = '0 '
      + this.getThemeSetting(`design.products.${this.instrumentName}.screen.indicator.gap.horizontal`, 0) + 'px '
      + this.getThemeSetting(`design.products.${this.instrumentName}.screen.indicator.margin.bottom`, 0) + 'px';
    // Ensure the value label appears on top of the min/max labels. The zindex assigned here
    // works because the indicator container's CSS display is "flex". (Non-static and flex elements obey
    // zindex order.)
    this.indicator.view.style.zIndex = '1';

    // Create min/max labels (on the left and right of the indicator)
    this.minLabel = cloneInstance(this.themeID, 'minmax_label_txt');
    this.minLabel.classList.add('minLabel', 'constrainHeight');
    this.maxLabel = cloneInstance(this.themeID, 'minmax_label_txt');
    this.maxLabel.classList.add('maxLabel', 'constrainHeight');
    let minmaxLabelHAlign = this.getThemeSetting(`design.products.${this.instrumentName}.screen.minmax_label.align.horizontal`);
    if (minmaxLabelHAlign === 'center') {
      this.minLabel.style.textAlign = 'center';
      this.maxLabel.style.textAlign = 'center';
    } else if (minmaxLabelHAlign === 'outer') {
      this.minLabel.style.textAlign = 'left';
      this.maxLabel.style.textAlign = 'right';
    } else {
      // Treat default or missing minmax label halign as "inner"
      this.minLabel.style.textAlign = 'right';
      this.maxLabel.style.textAlign = 'left';
    }

    // Create indicator's value label
    this.valueLabel = new InstrumentValueLabelComponent(this.themeID, this.instrumentName);
    let valueLabelMargin = this.getThemeSetting(`design.products.${this.instrumentName}.screen.value_label.margin.horizontal`);
    if (notNil(valueLabelMargin)) {
      this.valueLabel.setValueLabelMargin(valueLabelMargin);
    }
    let valueLabelTopMargin = this.getThemeSetting(`design.products.${this.instrumentName}.screen.value_label.margin.top`);
    if (notNil(valueLabelTopMargin)) {
      this.valueLabel.setValueLabelTopMargin(valueLabelTopMargin);
    }

    // Add indicator elements to indicatorContainer
    this.indicatorContainer.append(this.minLabel, this.indicator.view, this.maxLabel);

    // Append all content to view
    this.view.append(this.indicatorContainer);
    this.view.append(this.finalResultContainer);
  }

  /**
   * Updates the on-screen display of indicator and indicator-value label.
   */
  // TODO: Consider renaming this method to "setValue()".
  updateAverageVote () {
    // TODO: Move all 'valid' checks to RunView. I.e., in RunView, if a component is not valid, don't attempt to
    //  call methods on it. Then remove this 'valid' check.
    if (!this.valid) {
      return;
    }
    let averageAsPercentage = getAverageVote(MegaScreen.globals.appState.votes);
    this.enableAnimation();

    // Update indicator (slider bar, dial)
    this.indicator.setValue(averageAsPercentage);
  }

  disableAnimation () {
    this.indicator.disableAnimation();
  }

  enableAnimation () {
    this.indicator.enableAnimation();
  }

  resetAverageVote () {
    if (!this.valid) {
      return;
    }
    this.disableAnimation();
    this.indicator.setValue(0);
  }

  /**
   * Displays the average vote as text such as "$5 Million" in all elements specified by elementList.
   */
  displayAverageVote (elementList) {
    // TODO: Remove hard-coded references to MegaScreen, and extract the concepts of "answers" and
    //  votes so that InstrumentComponent can be used generically, without reference to MRTS concepts
    //  such as scenes, questions, answers, and votes.
    let textOfAverageAnswer = MegaScreen.globals.sceneService.getTextOfAverageAnswer(MegaScreen.globals.appState.effectiveScene, MegaScreen.globals.appState.votes);

    // Display output
    setText(elementList, textOfAverageAnswer);
    awaitRender().then(() => {
      elementList.forEach(element => {
        element.style.removeProperty('font-size');
        fitText(element, element.clientWidth, element.parentNode.clientHeight);
      });
    });
  }


  /**
   * Plays the intro animations.
   */
  playIntro () {
    let instrumentStartAnimation = cascadingGet(this.theme,
      [`design.products.${this.instrumentName}.screen.animation.main.start.label`,
        `design.default.screen.animation.main.start.label`]);
    let delay = cascadingGet(this.theme,
      [`design.products.${this.instrumentName}.screen.animation.main.start.delay`,
        `design.default.screen.animation.main.start.delay`]);

    if (notNil(instrumentStartAnimation)) {
      play(this.view, instrumentStartAnimation, delay);
    }
  }

  setAnswerText () {
    if (!this.valid) {
      return;
    }
    let answers = MegaScreen.globals.sceneService.getAnswers(MegaScreen.globals.appState.effectiveScene);
    if (answers.length > 0) {
      this.minLabel.textContent = answers[0].text;
      this.maxLabel.textContent = answers[answers.length-1].text;
      this.valueLabel.setSegmentLabels(answers.map(answer => answer.text));
      this.indicator.setNumSegments(answers.length);
      this.minLabel.style.removeProperty('font-size');
      this.maxLabel.style.removeProperty('font-size');
      awaitRender().then(() => {
        // TODO: Standardize use of parentNode or parentElement throught the code base
        fitText(this.minLabel, this.minLabel.clientWidth, this.minLabel.parentNode.clientHeight);
        fitText(this.maxLabel, this.maxLabel.clientWidth, this.maxLabel.parentNode.clientHeight);
      });
    }
  }


  /**
   * Displays the final vote result after the "polls are closed."
   */
  startSceneResult () {
    if (!this.valid) {
      return;
    }
    hide(this.indicatorContainer);
    show(this.finalResultContainer);
    let votes = MegaScreen.globals.appState.votes;
    this.displayAverageVote(this.finalResultContainer.querySelectorAll(`.${this.instrumentName}_result_txt, .averagevote_result_txt`));

    let instrumentResultAnimation = cascadingGet(this.theme,
      [`design.products.${this.instrumentName}.screen.animation.result.start.label`,
        `design.default.screen.animation.result.start.label`]);
    if (notNil(instrumentResultAnimation)) {
      play(this.finalResultContainer, instrumentResultAnimation);
    }
  }

  reset () {
    if (!this.valid) {
      return;
    }
    this.revertSceneResult();
    this.resetAverageVote();
  }

  revertSceneResult () {
    show(this.indicatorContainer);
    hide(this.finalResultContainer);
  }
}




















